import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParuresSalesRecap = () => {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [clients, setClients] = useState([]);
  const [housings, setHousings] = useState([]);
  const [missingParures, setMissingParures] = useState([]);
  const [editingParuresLocal, setEditingParuresLocal] = useState({});
  
  // Formulaire prestation
  const [formData, setFormData] = useState({
    date_prestation: '',
    client_id: '',
    housing_id: '',
    type_prestation: '',
    tarif: '',
    notes: ''
  });
  
  // Formulaire modification parures
  const [editFormData, setEditFormData] = useState({
    mission_id: '',
    parures_sales: ''
  });

  const prestationTypes = [
    "Lavage couette",
    "Lavage oreillers",
    "Nettoyage rideaux",
    "Lavage tapis",
    "Nettoyage canapé",
    "Autre"
  ];

  useEffect(() => {
    loadRecap();
    loadClientsAndHousings();
    loadMissingParures();
  }, [selectedMonth, selectedYear]);

  const loadRecap = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/parures-sales/monthly-recap/${selectedYear}/${selectedMonth}`);
      setRecap(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement du récapitulatif:", error);
      toast.info("Erreur lors du chargement du récapitulatif");
    } finally {
      setLoading(false);
    }
  };

  const loadMissingParures = async () => {
    try {
      const response = await axios.get(`${API}/parures-sales/missing/${selectedYear}/${selectedMonth}`);
      setMissingParures(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des parures manquantes:", error);
    }
  };

  const loadClientsAndHousings = async () => {
    try {
      const [clientsRes, housingsRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/housings`)
      ]);
      setClients(clientsRes.data);
      setHousings(housingsRes.data);
    } catch (error) {
      console.error("Erreur chargement clients/logements:", error);
    }
  };

  const handleOpenEditModal = async (missionDate, housingId) => {
    try {
      // Récupérer les missions de cette date pour ce logement
      const response = await axios.get(`${API}/missions`, {
        params: {
          date: missionDate,
          housing_id: housingId
        }
      });
      
      if (response.data && response.data.length > 0) {
        const missionWithDetails = response.data[0];
        const mission = missionWithDetails.mission; // Extraire l'objet mission
        
        setEditingMission(mission);
        setEditFormData({
          mission_id: mission.id,
          parures_sales: mission.parures_sales !== null ? mission.parures_sales.toString() : ''
        });
        setShowEditModal(true);
      } else {
        toast.warning("Mission non trouvée");
      }
    } catch (error) {
      console.error("Erreur chargement mission:", error);
      toast.error("Erreur lors du chargement de la mission");
    }
  };

  const handleUpdateParures = async (e) => {
    e.preventDefault();
    
    const paruresValue = editFormData.parures_sales === '' ? null : parseInt(editFormData.parures_sales);
    
    try {
      await axios.patch(`${API}/missions/${editFormData.mission_id}/parures-sales`, null, {
        params: { parures_sales: paruresValue }
      });
      
      toast.success("Nombre de parures mis à jour avec succès");
      setShowEditModal(false);
      setEditingMission(null);
      await loadRecap();
    } catch (error) {
      console.error("Erreur mise à jour parures:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleQuickUpdateParures = async (missionId) => {
    const paruresValue = editingParuresLocal[missionId];
    
    if (paruresValue === undefined || paruresValue === '') {
      toast.warning("Veuillez entrer un nombre de parures");
      return;
    }
    
    try {
      await axios.patch(`${API}/missions/${missionId}/parures-sales`, null, {
        params: { parures_sales: parseInt(paruresValue) }
      });
      
      toast.success("Nombre de parures mis à jour avec succès");
      setEditingParuresLocal({});
      await loadMissingParures();
      await loadRecap();
    } catch (error) {
      console.error("Erreur mise à jour parures:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddPrestation = async (e) => {
    e.preventDefault();
    
    if (!formData.date_prestation || !formData.client_id || !formData.housing_id || 
        !formData.type_prestation || !formData.tarif) {
      toast.warning("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      await axios.post(`${API}/prestations-supplementaires`, {
        date_prestation: formData.date_prestation,
        client_id: formData.client_id,
        housing_id: formData.housing_id,
        type_prestation: formData.type_prestation,
        tarif: parseFloat(formData.tarif),
        notes: formData.notes || null
      });

      setShowModal(false);
      resetForm();
      await loadRecap();
      toast.success("Prestation ajoutée avec succès");
    } catch (error) {
      console.error("Erreur ajout prestation:", error);
      toast.error("Erreur lors de l'ajout de la prestation");
    }
  };

  const handleDeletePrestation = async (prestationId) => {
    try {
      await axios.delete(`${API}/prestations-supplementaires/${prestationId}`);
      await loadRecap();
      toast.success("Prestation supprimée");
    } catch (error) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      date_prestation: '',
      client_id: '',
      housing_id: '',
      type_prestation: '',
      tarif: '',
      notes: ''
    });
  };

  const filteredHousings = formData.client_id 
    ? housings.filter(h => h.client_id === formData.client_id)
    : [];

  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-3 px-4">
      {/* Header */}
      <div className="card mb-3">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">🛏️ Récapitulatif Parures Sales - Facturation</h1>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary"
              >
                ➕ Ajouter une prestation
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="form-input"
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="form-input"
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau récapitulatif des parures non renseignées */}
      {missingParures && missingParures.length > 0 && (
        <div className="card mb-4 border-l-4 border-orange-500">
          <div className="card-header bg-gradient-to-r from-orange-50 to-amber-50">
            <h2 className="text-xl font-bold text-orange-800">
              ⚠️ Parures sales non renseignées ({missingParures.length})
            </h2>
            <p className="text-sm text-orange-600 mt-1">
              Logements où les employés n'ont pas indiqué le nombre de parures sales
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Client</th>
                  <th className="px-4 py-2 text-left">Logement</th>
                  <th className="px-4 py-2 text-center">Nb Parures</th>
                  <th className="px-4 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {missingParures.map((item) => (
                  <tr key={item.mission_id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {new Date(item.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-2 font-medium text-gray-900">
                      {item.client_name}
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-gray-800">{item.housing_pseudo}</div>
                      <div className="text-xs text-gray-500">{item.housing_address}</div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        value={editingParuresLocal[item.mission_id] || ''}
                        onChange={(e) => setEditingParuresLocal({
                          ...editingParuresLocal,
                          [item.mission_id]: e.target.value
                        })}
                        className="form-input w-20 text-center"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        onClick={() => handleQuickUpdateParures(item.mission_id)}
                        className="btn-primary text-xs px-3 py-1"
                      >
                        ✅ Enregistrer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Récapitulatif */}
      {recap && (
        <div className="space-y-3">
          {/* Total général */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold">Total Général : {recap.total_general}€</h2>
            <p className="text-green-100 mt-2">
              {monthNames[selectedMonth - 1]} {selectedYear}
            </p>
          </div>

          {/* Par client */}
          <div className="space-y-8">
            {recap.clients && recap.clients.length > 0 ? (
              recap.clients.map((client, clientIndex) => (
              <div 
                key={client.client_id} 
                className="card border-4 border-blue-300 shadow-lg hover:shadow-xl transition-shadow duration-300"
                style={{
                  borderLeftColor: clientIndex % 3 === 0 ? '#3B82F6' : clientIndex % 3 === 1 ? '#8B5CF6' : '#EC4899',
                  borderLeftWidth: '6px'
                }}
              >
                {/* En-tête client avec style amélioré */}
                <div 
                  className="px-6 py-4 border-b-4 border-gray-300"
                  style={{
                    background: clientIndex % 3 === 0 
                      ? 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)'
                      : clientIndex % 3 === 1
                      ? 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)'
                      : 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)'
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">
                        👤 {client.client_name}
                      </h2>
                      <p className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-white shadow-sm">
                          🏠 {client.housings.length} logement{client.housings.length > 1 ? 's' : ''}
                        </span>
                      </p>
                    </div>
                    <div className="text-right bg-white px-6 py-3 rounded-lg shadow-md">
                      <p className="text-3xl font-extrabold text-blue-600">{client.total_client}€</p>
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Total client</p>
                    </div>
                  </div>
                </div>

                {/* Logements du client */}
                <div className="card-body space-y-2 py-2">
                  {client.housings.map((housing) => (
                    <div key={housing.housing_id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* En-tête logement */}
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-gray-900">{housing.housing_pseudo}</h3>
                            <p className="text-sm text-gray-600">{housing.housing_address}</p>
                            
                            {/* Message si parures incluses dans tarif ménage */}
                            {housing.parures_incluses_tarif_menage ? (
                              <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✅ Tarif parures inclus dans le ménage
                              </div>
                            ) : (
                              <div className="flex space-x-4 mt-2 text-xs text-gray-600">
                                <span>💰 Tarif par parure: {housing.tarif_base || 0}€</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-gray-900">{housing.total_montant}€</p>
                            <p className="text-xs text-gray-500">{housing.total_parures} parure(s)</p>
                          </div>
                        </div>
                      </div>

                      {/* Tableau des missions et prestations */}
                      <div className="overflow-x-auto">
                        <table className="table-auto w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-1 text-left">Date</th>
                              <th className="px-4 py-1 text-left">Type</th>
                              <th className="px-4 py-1 text-center">Nb Parures</th>
                              <th className="px-4 py-1 text-center">Différence</th>
                              <th className="px-4 py-1 text-right">Montant</th>
                              <th className="px-4 py-1 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {housing.missions.length > 0 ? (
                              housing.missions.map((mission, idx) => (
                                <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="px-4 py-1">{new Date(mission.date).toLocaleDateString('fr-FR')}</td>
                                  <td className="px-4 py-1">
                                    {mission.type === 'prestation' ? (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                        🔧 {mission.type_prestation}
                                      </span>
                                    ) : (
                                      <span className="text-gray-600">Parures sales</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-1 text-center font-medium">
                                    {mission.nb_parures !== null ? mission.nb_parures : '-'}
                                  </td>
                                  <td className="px-4 py-1 text-center">
                                    {mission.difference !== null ? (
                                      <>
                                        {mission.difference > 0 && (
                                          <span className="text-green-600 font-medium">+{mission.difference}</span>
                                        )}
                                        {mission.difference < 0 && (
                                          <span className="text-red-600 font-medium">{mission.difference}</span>
                                        )}
                                        {mission.difference === 0 && (
                                          <span className="text-gray-500">0</span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-1 text-right font-medium">{mission.montant}€</td>
                                  <td className="px-4 py-1 text-center">
                                    {mission.type === 'prestation' ? (
                                      <button
                                        onClick={() => handleDeletePrestation(mission.prestation_id)}
                                        className="text-red-600 hover:text-red-800 text-sm"
                                        title="Supprimer"
                                      >
                                        🗑️
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleOpenEditModal(mission.date, housing.housing_id)}
                                        className="btn-secondary text-xs px-2 py-1"
                                        title="Modifier le nombre de parures"
                                      >
                                        ✏️ Modifier
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="px-4 py-4 text-center text-gray-500">
                                  Aucune donnée pour ce logement
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              ))
            ) : (
              <div className="card">
                <div className="card-body text-center text-gray-500 py-8">
                  Aucune donnée de parures sales pour ce mois
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Modifier Parures */}
      {showEditModal && editingMission && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">✏️ Modifier le nombre de parures sales</h3>
              <button 
                onClick={() => { setShowEditModal(false); setEditingMission(null); }} 
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateParures} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Date :</strong> {new Date(editingMission.date_mission).toLocaleDateString('fr-FR')}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Valeur actuelle :</strong> {editingMission.parures_sales !== null ? `${editingMission.parures_sales} parures` : 'Non renseigné'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau nombre de parures sales
                </label>
                <input
                  type="number"
                  min="0"
                  value={editFormData.parures_sales}
                  onChange={(e) => setEditFormData({ ...editFormData, parures_sales: e.target.value })}
                  className="form-input w-full"
                  placeholder="Nombre de parures..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Laissez vide pour supprimer la valeur
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingMission(null); }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  ✅ Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ajouter Prestation */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">➕ Ajouter une prestation supplémentaire</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                ✕
              </button>
            </div>

            <form onSubmit={handleAddPrestation}>
              <div className="space-y-4">
                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de la prestation *
                  </label>
                  <input
                    type="date"
                    value={formData.date_prestation}
                    onChange={(e) => setFormData(prev => ({...prev, date_prestation: e.target.value}))}
                    className="form-input"
                    required
                  />
                </div>

                {/* Client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client *
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData(prev => ({...prev, client_id: e.target.value, housing_id: ''}))}
                    className="form-input"
                    required
                  >
                    <option value="">-- Sélectionner un client --</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.prenom} {client.nom}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Logement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logement *
                  </label>
                  <select
                    value={formData.housing_id}
                    onChange={(e) => setFormData(prev => ({...prev, housing_id: e.target.value}))}
                    className="form-input"
                    disabled={!formData.client_id}
                    required
                  >
                    <option value="">-- Sélectionner un logement --</option>
                    {filteredHousings.map(housing => (
                      <option key={housing.id} value={housing.id}>
                        {housing.pseudo || housing.adresse}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Type de prestation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de prestation *
                  </label>
                  <select
                    value={formData.type_prestation}
                    onChange={(e) => setFormData(prev => ({...prev, type_prestation: e.target.value}))}
                    className="form-input"
                    required
                  >
                    <option value="">-- Sélectionner --</option>
                    {prestationTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {formData.type_prestation === "Autre" && (
                    <input
                      type="text"
                      placeholder="Précisez le type de prestation"
                      onChange={(e) => setFormData(prev => ({...prev, type_prestation: e.target.value}))}
                      className="form-input mt-2"
                    />
                  )}
                </div>

                {/* Tarif */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tarif (€) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.tarif}
                    onChange={(e) => setFormData(prev => ({...prev, tarif: e.target.value}))}
                    className="form-input"
                    placeholder="0.00"
                    required
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optionnel)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                    rows={2}
                    className="form-textarea"
                    placeholder="Notes additionnelles..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  ✅ Ajouter la prestation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParuresSalesRecap;
